import {$s, logger} from '@bitstillery/common/app'
import {current_account_slug} from '@bitstillery/common/lib/account'
import {read_ssr_data, record_ssr_data} from '@bitstillery/common/lib/ssr_data'
import {unchecked} from '@bitstillery/common/lib/unchecked'

/**
 * A generic memoization layer that sits *above* `api` (the transport). `cached(name, loader, opts)`
 * wraps a data-access function — one that usually calls `api.get` and reshapes the result — and gives
 * it, in one primitive:
 *
 *  - in-flight de-duplication (concurrent callers share a single request; e.g. many `Card`s mounting),
 *  - a TTL store (process-wide on the SSR server, tab-wide on the client),
 *  - `scope`-keyed isolation so a value is only reused where it is safe to (public vs per-user/account),
 *  - a synchronous `peek()` for warm reads (what makes SSR render placeholders/fun-facts with JS off),
 *  - an automatic SSR→client handoff so the value the server rendered seeds the client's first render,
 *  - `clear()` + a registry so the running SSR process can be told to drop caches (see `clear_cache`).
 *
 * The `loader` itself knows nothing about caching — it just fetches. `cached()` decides *whether* to
 * call it. Terse `[cache <ssr|web>]` debug lines trace every path (miss/hit/wait/store/seed/peek/…).
 */

/** What a cached value may be shared across. `user` is the safe default: never reused between users. */
export type CacheScope = 'public' | 'user' | 'account'

export interface CacheOptions<A extends unknown[] = unknown[], R = unknown> {
    /** Time-to-live in ms; entries are treated as missing once expired (lazy eviction on read). */
    ttl: number
    /** What varies the key beyond the arguments. Defaults to `'user'`. */
    scope?: CacheScope
    /** Derives the argument-dependent part of the key (kept out of logs is safe). Defaults to JSON of args. */
    key?: (...args: A) => string
    /** Carry the value across the SSR boundary so the client's first render reuses it. Default `true`. */
    handoff?: boolean
    /** Only store (and hand off) values that pass this test — e.g. skip empties so callers can retry. Default: store all. */
    store_if?: (value: R) => boolean
}

export interface Cached<A extends unknown[], R> {
    /** Cached async call: peek → in-flight → loader. */
    (...args: A): Promise<R>
    /** Synchronous read of a live (unexpired) value, or `undefined`. Records for the SSR handoff. */
    peek(...args: A): R | undefined
    /** How many loads are in flight. Tells a caller whether the data it set in motion has settled. */
    pending(): number
    /** Store a value the caller already has in hand, as if the loader had just produced it. */
    prime(args: A, value: R): void
    /** Drop every entry for this cache. */
    clear(): void
}

interface Entry<R> {
    value: R
    expires: number
}

/** Named caches, so a single endpoint can clear one or all of them in the running SSR process. */
const registry = new Map<string, Pick<Cached<unknown[], unknown>, 'clear' | 'pending'>>()

function log(name: string, key: string, event: string): void {
    const origin = globalThis.__SSR_MODE__ ? 'ssr' : 'web'
    logger.debug(`[cache ${origin}] ${name} ${key || '·'} ${event}`)
}

/** The part of the key that isolates a value to those allowed to share it. */
function scope_discriminator(scope: CacheScope): string {
    if (scope === 'public') return ''
    if (scope === 'account') return current_account_slug()
    return $s.identity.token ?? '' // 'user'
}

export function cached<A extends unknown[], R>(
    name: string,
    loader: (...args: A) => Promise<R>,
    options: CacheOptions<A, R>,
): Cached<A, R> {
    const {ttl, scope = 'user', handoff = true} = options
    const arg_key = options.key ?? ((...args: A) => (args.length ? JSON.stringify(args) : ''))
    const store_if = options.store_if ?? (() => true)

    // Process-wide on the SSR server (keeps SSR warm), tab-wide on the client.
    const store = new Map<string, Entry<R>>()
    const inflight = new Map<string, Promise<R>>()

    // Full key = this cache (name, unique) + what a value may be shared across (scope) + the
    // arguments. The `name` prefix keeps the shared SSR handoff map from colliding across caches that
    // happen to produce the same arg key (e.g. several no-arg public caches). `readable` (no scope
    // discriminator, i.e. no token) is what we log.
    const keys = (args: A) => {
        const readable = arg_key(...args)
        return {full: `${name}|${scope_discriminator(scope)}|${readable}`, readable}
    }

    // Carry a server-produced value to the client's first render. No-op off SSR and when disabled.
    const carry = handoff ? (full: string, value: R) => record_ssr_data(full, value) : () => {}

    // A live (unexpired) entry, seeding from the SSR handoff on first client access.
    const live = (full: string, readable: string): Entry<R> | undefined => {
        let entry = store.get(full)
        if (!entry && handoff) {
            // Browser-only; no-op on the server. What the server recorded under this cache's own key.
            const seeded = unchecked<R | undefined>(read_ssr_data(full))
            if (seeded !== undefined) {
                entry = {value: seeded, expires: Date.now() + ttl}
                store.set(full, entry)
                log(name, readable, 'seed')
            }
        }
        if (!entry) return undefined
        if (entry.expires <= Date.now()) {
            store.delete(full)
            return undefined
        }
        return entry
    }

    const call = (...args: A): Promise<R> => {
        const {full, readable} = keys(args)
        const hit = live(full, readable)
        if (hit) {
            log(name, readable, 'hit')
            carry(full, hit.value)
            return Promise.resolve(hit.value)
        }
        const pending = inflight.get(full)
        if (pending) {
            log(name, readable, 'wait')
            return pending
        }
        log(name, readable, 'miss')
        const promise = loader(...args)
            .then((value) => {
                inflight.delete(full)
                if (store_if(value)) {
                    store.set(full, {value, expires: Date.now() + ttl})
                    carry(full, value)
                    log(name, readable, 'store')
                } else {
                    log(name, readable, 'skip')
                }
                return value
            })
            .catch((error) => {
                inflight.delete(full)
                log(name, readable, 'fail')
                throw error
            })
        inflight.set(full, promise)
        return promise
    }

    const peek = (...args: A): R | undefined => {
        const {full, readable} = keys(args)
        const hit = live(full, readable)
        // Warm SSR renders happen through peek() (fire-and-forget oninit), so this is where we carry
        // the value to the client. Not logged — many components peek the same key every render, which
        // floods the console; the interesting transitions (miss/store/seed) are logged elsewhere.
        if (hit && globalThis.__SSR_MODE__) {
            carry(full, hit.value)
        }
        return hit?.value
    }

    const prime = (args: A, value: R): void => {
        const {full, readable} = keys(args)
        store.set(full, {value, expires: Date.now() + ttl})
        carry(full, value)
        log(name, readable, 'prime')
    }

    const pending_count = () => inflight.size

    const clear = () => {
        store.clear()
        inflight.clear()
        log(name, '', 'clear')
    }

    const fn: Cached<A, R> = Object.assign(call, {clear, peek, pending: pending_count, prime})

    if (registry.has(name)) {
        throw new Error(`api_cache: duplicate cache name "${name}"`)
    }
    registry.set(name, fn)
    return fn
}

/** Clear one named cache in this process. Returns `false` if no cache by that name is registered. */
export function clear_cache(name: string): boolean {
    const cache = registry.get(name)
    if (!cache) return false
    cache.clear()
    return true
}

/** Clear every registered cache in this process. */
export function clear_all(): void {
    logger.debug(`[cache] clear all (${registry.size})`)
    for (const cache of registry.values()) cache.clear()
}

/**
 * Loads in flight across every cache. Lets a caller that set work in motion tell when it has landed
 * without having to know which caches it touched — a CMS page loads its rows through `async-data`, but
 * the images on them come from the media caches.
 */
export function pending_loads(): number {
    let total = 0
    for (const cache of registry.values()) {
        total += cache.pending()
    }
    return total
}

/** Names of all registered caches (e.g. for a clear endpoint to expose). */
export function cache_names(): string[] {
    return [...registry.keys()]
}
